import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
    AccessoryCharge,
    CartDescriptionException,
    CartEvents,
    CartException,
    CartExceptionType,
    Charge,
    Exception,
    Facility,
    PaymentCart,
    PaymentCartRequest,
    PaymentCartType,
    PaymentRequestException,
    PaymentRequestExceptionType,
    PaymentType,
} from '@cargos/sprintpay-models';
import { CartService, TaxAddressPayload } from '@cargos/sprintpay-services';
import { TaxEligibleResponse } from '@cargos/sprintpay-ui';
import {
    Observable,
    catchError,
    defaultIfEmpty,
    distinctUntilChanged,
    forkJoin,
    from,
    map,
    of,
    switchMap,
    take,
    throwError,
} from 'rxjs';
import Swal from 'sweetalert2';
import { Shipment } from '../models/shipment';
import { ApprovalFlowLevelsService } from './approval-flow-levels.service';
import { CartHandlerService } from './cart-handler.service';
import { CartBillService } from './cart.service';
import { ConfigService } from './config.service';
import { CustomerService } from './customer.service';
import { DynamicsFieldsService } from './dynamic-field.service';
import { ExtraItemsService } from './extra-items.service';
import { ISCAndStorageCartService } from './isc-storage.service';
import { PaymentCartBuilder } from './payment-builder';
import { PromoCodesService } from './promo-code.service';
import { SummaryService } from './summary/summary.service';

@Injectable({
    providedIn: 'root',
})
export class PaymentRequestService {
    constructor(
        private cartBillService: CartBillService,
        private configService: ConfigService,
        private iscAndStorageCartService: ISCAndStorageCartService,
        private matSnackBar: MatSnackBar,
        private cartService: CartService,
        private summaryService: SummaryService,
        private extraItemsService: ExtraItemsService,
        private approvalFlowService: ApprovalFlowLevelsService,
        private promoCodesService: PromoCodesService,
        private customerService: CustomerService,
        private dynamicFieldsService: DynamicsFieldsService,
        private paymentCartBuilder: PaymentCartBuilder,
        private cartHandlerService: CartHandlerService
    ) {}

    /**
     * Adds a charge to the cart.
     *
     * @param charge The charge to be added.
     * @returns An observable that emits an array of payment carts or a single payment cart.
     */
    addChargeToCart(charge: Charge, billingInfo?: TaxAddressPayload): Observable<PaymentCart[] | PaymentCart> {
        return this.paymentCartBuilder.buildChargeBeforeAdd$(charge).pipe(
            switchMap((paymentRequestUpdated: PaymentCartRequest) => {
                return this.sendRequestByTypeOfUser(paymentRequestUpdated, true, billingInfo);
            })
        );
    }

    /**
     * Adds an accessory to the cart and sends the payment request based on the user type.
     *
     * @param accessoryCharge The accessory charge to be added to the cart.
     * @returns An Observable that emits the updated PaymentCart or an array of PaymentCart objects.
     */
    addAccessoryToCart(
        accessoryCharge: AccessoryCharge,
        billingInfo?: TaxAddressPayload
    ): Observable<PaymentCart[] | PaymentCart> {
        return this.paymentCartBuilder.buildAccessoryBeforeAdd(accessoryCharge).pipe(
            switchMap((paymentRequestUpdated: PaymentCartRequest) => {
                return this.sendRequestByTypeOfUser(paymentRequestUpdated, true, billingInfo);
            })
        );
    }

    /**
     * Adds a shipment to the cart and sends the payment request based on the user type.
     * @param shipment The shipment to be added to the cart.
     * @returns An Observable that emits the updated PaymentCart or an array of PaymentCart objects.
     */
    addShipmentToCart(shipment: Shipment, billingInfo?: TaxAddressPayload): Observable<PaymentCart[] | PaymentCart> {
        return this.paymentCartBuilder.buildShipmentBeforeAdd(shipment).pipe(
            switchMap((paymentRequestUpdated: PaymentCartRequest) => {
                return this.sendRequestByTypeOfUser(paymentRequestUpdated, true, billingInfo);
            })
        );
    }

    editPaymentRequest<T = Facility>(paymentCart: PaymentCartType<T>, emitChange = true): Observable<PaymentCart[]> {
        return this.cartService.editPaymentRequest(paymentCart).pipe(
            switchMap((paymentCart: PaymentCart[]) =>
                this.cartHandlerService.getCartRequest$().pipe(
                    take(1),
                    map((cart) => {
                        this.cartBillService.setCart(cart);
                        return paymentCart;
                    })
                )
            ),
            map((paymentCart: PaymentCart[]) => {
                if (emitChange) {
                    this.cartBillService.newEvent(CartEvents.paymentRequestAdded);
                }
                return paymentCart;
            })
        );
    }

    getDisccount(): Observable<number | null> {
        return of(this.configService.isPromoCodeEnable()).pipe(
            switchMap((isPromoCodeEnable) => {
                if (isPromoCodeEnable) {
                    return this.promoCodesService.getAllNonReservationFee$().pipe(distinctUntilChanged());
                }

                return of(null);
            })
        );
    }

    addPaymentRequest(
        paymentCart: PaymentCartRequest,
        emitChange = true,
        billingInfo?: TaxAddressPayload
    ): Observable<PaymentCart[]> {
        return this.validationsBeforeAddPayment(paymentCart).pipe(
            switchMap(() =>
                this.updatePaymentRequestBeforeAdd(paymentCart).pipe(
                    map((paymentCart) => {
                        if (this.customerService.isGuest() && !this.configService.isNotificationEmailMapped()) {
                            paymentCart.notificationEmail = null;
                        }
                        return paymentCart;
                    })
                )
            ),
            switchMap((paymentCartUpdated) => {
                return this.cartService.addPaymentRequest(paymentCartUpdated, billingInfo).pipe(
                    take(1),
                    switchMap((response) => {
                        if (response.every((payment) => this.isTaxElegibleResponse(payment))) {
                            return throwError(() =>
                                PaymentRequestException.fromJson<TaxEligibleResponse[]>({
                                    code: PaymentRequestExceptionType.TAX_ELIGIBLE,
                                    error: response,
                                })
                            );
                        }

                        return of(response);
                    }),
                    switchMap((paymentCart: PaymentCart[]) =>
                        this.cartHandlerService.getCartRequest$().pipe(
                            take(1),
                            map(() => {
                                return paymentCart;
                            })
                        )
                    ),
                    map((paymentCart) => {
                        if (emitChange) {
                            this.cartBillService.newEvent(CartEvents.paymentRequestAdded);
                        }
                        return paymentCart;
                    })
                );
            }),
            catchError((error: Exception) => {
                if (error instanceof PaymentRequestException) {
                    return throwError(() => error);
                }

                const customError = this.getCustomPaymentError(error);

                return throwError(() => customError);
            })
        );
    }

    getCustomPaymentError(error: Exception): Exception {
        if (error?.description?.includes('AutoPay')) {
            error.title = 'AutoPay Alert';
        }

        return Exception.fromJson(error);
    }

    sendRequestByTypeOfUser(
        paymentCart: PaymentCartRequest,
        emitChange = true,
        billingInfo?: TaxAddressPayload
    ): Observable<PaymentCart[] | PaymentCart> {
        if (this.configService.isEnableRequestorApprovalFlow()) {
            const hasApprovalFlowLevels = this.approvalFlowService.getLevelsOfApprovalFlowByAmount(
                paymentCart.amount || 0
            );

            return hasApprovalFlowLevels > 0
                ? this.submitPaymentRequest(paymentCart, emitChange, billingInfo)
                : this.addPaymentRequest(paymentCart, emitChange, billingInfo);
        }

        return this.addPaymentRequest(paymentCart, emitChange, billingInfo);
    }

    submitPaymentRequest(
        paymentCart: PaymentCartRequest,
        emitChange = true,
        billingInfo?: TaxAddressPayload
    ): Observable<PaymentCart> {
        return this.validationsBeforeAddPayment(paymentCart).pipe(
            switchMap(() => this.updatePaymentRequestBeforeAdd(paymentCart)),
            switchMap((paymentCartUpdated) => {
                const requestAddedToCart = this.approvalFlowService.isChargeToPayAvailable(
                    paymentCartUpdated.amount || 0
                );
                const firstLocation =
                    this.customerService.getFirstLocationId() ||
                    Number(this.dynamicFieldsService.getCollectorFormValueByKey('locationId'));
                return this.cartService.startApprovalflow(paymentCartUpdated, firstLocation, billingInfo).pipe(
                    take(1),
                    switchMap((response) => {
                        if (this.isTaxElegibleResponse(response)) {
                            return throwError(() =>
                                PaymentRequestException.fromJson<TaxEligibleResponse>({
                                    code: PaymentRequestExceptionType.TAX_ELIGIBLE,
                                    error: response,
                                })
                            );
                        }

                        return of(response);
                    }),
                    switchMap((paymentCart: PaymentCart) => {
                        if (requestAddedToCart) {
                            return this.cartHandlerService.getCartRequest$().pipe(
                                take(1),
                                map(() => {
                                    return paymentCart;
                                })
                            );
                        }
                        this.cartBillService.setAndSaveSubmittedItems(paymentCartUpdated);
                        return of(paymentCart);
                    }),
                    map((response) => {
                        if (emitChange) {
                            this.cartBillService.newEvent(CartEvents.paymentRequestAdded);
                        }
                        return response;
                    })
                );
            }),
            catchError((error: Exception) => {
                if (error instanceof PaymentRequestException) {
                    return throwError(() => error);
                }

                const customError = this.getCustomPaymentError(error);

                return throwError(() => customError);
            })
        );
    }

    addPaymentRequests(paymentsCart: PaymentCartRequest[]): Observable<PaymentCart[]> {
        return forkJoin(paymentsCart.map((payment: PaymentCartRequest) => this.addPaymentRequest(payment, false))).pipe(
            defaultIfEmpty([]),
            switchMap((paymentCart: PaymentCart[][]) =>
                this.cartHandlerService.getCartRequest$().pipe(
                    take(1),
                    map(() => {
                        return paymentCart;
                    })
                )
            ),
            map((response: PaymentCart[][]) => {
                this.cartBillService.newEvent(CartEvents.paymentRequestAdded);

                return response.flat();
            })
        );
    }

    removePaymentRequestIds(paymentRequestIds: number[]): Observable<PaymentCart[]> {
        return forkJoin(
            paymentRequestIds.map((paymentRequestId: number) =>
                this.cartBillService.getPaymentCartById$(paymentRequestId).pipe(
                    take(1),
                    switchMap((paymentRequest) => {
                        if (!paymentRequest) {
                            return of(null);
                        }

                        return this.validationsBeforeRemovePayment(paymentRequest).pipe(
                            map((deletePayment) => {
                                if (deletePayment && paymentRequest.isAccessorial) {
                                    this.extraItemsService.removeCharge(paymentRequest);
                                }
                                return deletePayment ? paymentRequestId : null;
                            })
                        );
                    })
                )
            )
        ).pipe(
            map((validatedIds) => validatedIds.filter((id) => id !== null) as number[]),
            defaultIfEmpty([]),
            switchMap((idsToDelete) => {
                return this.cartService.deletePaymentRequestIds(idsToDelete);
            })
        );
    }

    removePaymentRequest(paymentRequestId: number, emitChange = true): Observable<PaymentCart[]> {
        return this.cartBillService.getPaymentCartById$(paymentRequestId).pipe(
            take(1),
            switchMap((paymentRequest) => {
                if (!paymentRequest) {
                    return of([]);
                }

                return this.validationsBeforeRemovePayment(paymentRequest).pipe(
                    switchMap((deletePayment) => {
                        if (deletePayment) {
                            if (paymentRequest.isAccessorial) {
                                this.extraItemsService.removeCharge(paymentRequest);
                            }
                            return this.cartService.removePaymentRequest(paymentRequestId);
                        }

                        return of([]);
                    })
                );
            }),
            switchMap((paymentCart: PaymentCart[]) =>
                this.cartHandlerService.getCartRequest$().pipe(
                    take(1),
                    map(() => {
                        return paymentCart;
                    })
                )
            ),
            map((paymentCart) => {
                if (emitChange) {
                    this.cartBillService.newEvent(CartEvents.paymentRequestRemoved);
                }

                return paymentCart;
            })
        );
    }

    removePaymentRequests(paymentRequestIds: number[]): Observable<PaymentCart[][]> {
        return this.removePaymentRequestIds(paymentRequestIds).pipe(
            map((paymentCart) => [paymentCart]),
            switchMap((paymentCart: PaymentCart[][]) =>
                this.cartHandlerService.getCartRequest$().pipe(
                    take(1),
                    map(() => {
                        this.extraItemsService.resetAccessoryAdded();
                        return paymentCart;
                    })
                )
            ),
            map((paymentsCart) => {
                this.cartBillService.newEvent(CartEvents.paymentRequestRemoved);
                return paymentsCart;
            })
        );
    }

    validationsBeforeAddPayment(paymentCart: PaymentCartRequest): Observable<boolean> {
        return forkJoin([this.validateISCBeforeAddToCart(paymentCart).pipe(take(1))]).pipe(
            map((validations) => validations.every((validation) => validation === true))
        );
    }

    validateISCBeforeAddToCart(paymentCart: PaymentCartRequest): Observable<boolean> {
        return of(this.configService.isAllowedPaymentsWithoutISCPaid()).pipe(
            switchMap((isAllowedPaymentsWithoutISCPaid) => {
                if (isAllowedPaymentsWithoutISCPaid) {
                    return of(true);
                }

                return this.iscAndStorageCartService.validateISCBeforeAddToCart(paymentCart);
            })
        );
    }

    validationsBeforeRemovePayment(paymentCart: PaymentCart): Observable<boolean> {
        return forkJoin([this.validateISCBeforeRemoveToCart(paymentCart).pipe(take(1))]).pipe(
            map((validations) => validations.every((validation) => validation === true))
        );
    }

    validateISCBeforeRemoveToCart(paymentCart: PaymentCart): Observable<boolean> {
        return of(this.configService.isAllowedPaymentsWithoutISCPaid()).pipe(
            switchMap((isAllowedPaymentsWithoutISCPaid) => {
                if (isAllowedPaymentsWithoutISCPaid) {
                    return of(true);
                }

                return this.iscAndStorageCartService.validateISCBeforeRemoveToCart(paymentCart).pipe(
                    switchMap((isAllowedToContinue) => {
                        if (!isAllowedToContinue) {
                            return this.cartBillService.deleteCart(false).pipe(
                                map(() => {
                                    this.matSnackBar.open(
                                        CartDescriptionException[CartExceptionType.ISC_REQUIRED],
                                        '',
                                        {
                                            duration: 3000,
                                            panelClass: ['modal-snackbar'],
                                        }
                                    );

                                    return false;
                                }),
                                catchError(() =>
                                    throwError(() => {
                                        return CartException.fromJson({
                                            code: CartExceptionType.ISC_REQUIRED,
                                        });
                                    })
                                )
                            );
                        }

                        return of(isAllowedToContinue);
                    })
                );
            })
        );
    }

    getPaymentRequestStorageFromCart(hawb: string): Observable<PaymentCart | undefined> {
        return this.cartBillService.getCart$().pipe(
            map((cart) =>
                cart.find((paymentCart) => {
                    if (paymentCart.hawb) {
                        return (
                            paymentCart.externalData?.paymentType?.toLowerCase() === PaymentType.STORAGE &&
                            paymentCart.hawb === hawb
                        );
                    }

                    return paymentCart.externalData?.paymentType?.toLowerCase() === PaymentType.STORAGE;
                })
            )
        );
    }

    updatePaymentRequestBeforeAdd(paymentCart: PaymentCartRequest): Observable<PaymentCartRequest> {
        return this.addCostCenterToPaymentRequest(paymentCart);
    }

    addCostCenterToPaymentRequest(paymentCart: PaymentCartRequest): Observable<PaymentCartRequest> {
        return this.summaryService.getCostCenter$().pipe(
            take(1),
            map((costCenter) => {
                return {
                    ...paymentCart,
                    externalData: {
                        ...paymentCart.externalData,
                        externalVendorId: costCenter,
                    },
                };
            })
        );
    }

    deleteCartLib(ids: number[], isNeededConfirmation = true): Observable<CartEvents | null> {
        if (!isNeededConfirmation) {
            return this.deleteCartActionLib(ids);
        }

        return from(
            Swal.fire({
                title: 'Are you sure?',
                text: "You won't be able to revert this!",
                showCancelButton: true,
                showDenyButton: false,
                showConfirmButton: true,
                confirmButtonText: 'Yes, delete it!',
                customClass: {
                    confirmButton: 'deny',
                },
                reverseButtons: true,
            })
        ).pipe(
            switchMap((result) => {
                if (!result.value) {
                    return of(null);
                }

                return this.deleteCartActionLib(ids);
            })
        );
    }

    private deleteCartActionLib(ids: number[]) {
        return this.removePaymentRequests(ids).pipe(
            map(() => {
                this.cartBillService.newEvent(CartEvents.paymentRequestRemoved);
                return CartEvents.paymentRequestRemoved;
            })
        );
    }

    isTaxElegibleResponse(response: PaymentCart<Facility> | TaxEligibleResponse): response is TaxEligibleResponse {
        return 'taxEligible' in response;
    }
}
